/**
 * Anthropic provider adapter for ludicrous-gateway
 * Passes requests directly to Anthropic API with subscription passthrough
 */

import { BaseProvider } from "./base-provider.js";
import type {
  ProviderRequestContext,
  StreamHandler,
} from "../types/providers.js";
import type { MessagesRequest, MessagesResponse } from "../types/messages.js";
import type { ProviderConfig } from "../types/config.js";

/**
 * Anthropic API provider
 * Supports subscription passthrough for Claude Code clients
 */
export class AnthropicProvider extends BaseProvider {
  private subscriptionPassthrough: boolean;

  constructor(config: ProviderConfig) {
    super(config);
    this.subscriptionPassthrough = config.subscription_passthrough ?? true;
  }

  protected getDefaultBaseUrl(): string {
    return "https://api.anthropic.com";
  }

  /**
   * Build headers for Anthropic API
   * Passes through subscription headers if enabled
   */
  protected override buildHeaders(
    context: ProviderRequestContext
  ): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "anthropic-version": "2023-06-01",
    };

    if (this.subscriptionPassthrough) {
      // Pass through authentication headers from original request
      const authHeader = context.headers["authorization"];
      if (authHeader) {
        headers["Authorization"] = authHeader;
      }

      const apiKeyHeader = context.headers["x-api-key"];
      if (apiKeyHeader) {
        headers["x-api-key"] = apiKeyHeader;
      }

      // Pass through subscription-related headers
      const subscriptionHeaders = [
        "anthropic-beta",
        "anthropic-organization",
        "anthropic-client-id",
      ];

      for (const headerName of subscriptionHeaders) {
        const value = context.headers[headerName.toLowerCase()];
        if (value) {
          headers[headerName] = value;
        }
      }
    }

    return headers;
  }

  /**
   * Send a non-streaming request to Anthropic
   */
  async sendRequest(
    request: MessagesRequest,
    context: ProviderRequestContext
  ): Promise<MessagesResponse> {
    const startTime = Date.now();

    try {
      const url = `${this.baseUrl}/v1/messages`;
      const headers = this.buildHeaders(context);

      const transformedRequest = {
        ...request,
        model: context.resolvedModel,
        stream: false,
      };

      this.logger.debug("Sending request to Anthropic", {
        url,
        model: context.resolvedModel,
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        context.timeoutMs
      );

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(transformedRequest),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Anthropic API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      const latencyMs = Date.now() - startTime;

      this.recordRequest(true, latencyMs);
      this.logger.debug("Anthropic request successful", { latencyMs });

      return this.transformResponse(data);
    } catch (error) {
      const latencyMs = Date.now() - startTime;
      this.recordRequest(false, latencyMs);

      this.logger.error("Anthropic request failed", {
        error: error instanceof Error ? error.message : "Unknown error",
        latencyMs,
      });

      throw error;
    }
  }

  /**
   * Send a streaming request to Anthropic
   */
  async sendStreamingRequest(
    request: MessagesRequest,
    context: ProviderRequestContext,
    handler: StreamHandler
  ): Promise<void> {
    const startTime = Date.now();

    try {
      const url = `${this.baseUrl}/v1/messages`;
      const headers = this.buildHeaders(context);
      headers["Accept"] = "text/event-stream";

      const transformedRequest = {
        ...request,
        model: context.resolvedModel,
        stream: true,
      };

      this.logger.debug("Starting streaming request to Anthropic", {
        url,
        model: context.resolvedModel,
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        context.timeoutMs
      );

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(transformedRequest),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Anthropic API error: ${response.status} - ${errorText}`);
      }

      if (!response.body) {
        throw new Error("No response body for streaming request");
      }

      // Process SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let currentEventType: string | null = null;

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          const trimmedLine = line.trim();

          if (trimmedLine === "") {
            // Empty line marks end of event - reset event type
            currentEventType = null;
            continue;
          }

          if (trimmedLine.startsWith("event:")) {
            // Store event type for the upcoming data line
            currentEventType = trimmedLine.slice(6).trim();
          } else if (trimmedLine.startsWith("data:")) {
            const data = trimmedLine.slice(5).trim();

            // Skip empty data or [DONE] marker
            if (data === "" || data === "[DONE]") {
              continue;
            }

            // Use the event type if we have one, otherwise try to parse from data
            const eventType = currentEventType ?? "data";

            try {
              // Try to parse as JSON to extract the type
              const parsed = JSON.parse(data);
              const actualEventType = parsed.type ?? eventType;
              handler.onEvent(actualEventType, data);
            } catch {
              // If JSON parsing fails, still forward the raw data
              handler.onEvent(eventType, data);
            }
          }
          // Ignore other lines (comments starting with :, etc.)
        }
      }

      const latencyMs = Date.now() - startTime;
      this.recordRequest(true, latencyMs);
      handler.onComplete();

      this.logger.debug("Anthropic streaming request completed", { latencyMs });
    } catch (error) {
      const latencyMs = Date.now() - startTime;
      this.recordRequest(false, latencyMs);

      const err = error instanceof Error ? error : new Error("Unknown error");
      this.logger.error("Anthropic streaming request failed", {
        error: err.message,
        latencyMs,
      });

      handler.onError(err);
    }
  }

  /**
   * Health check for Anthropic API
   */
  override async healthCheck(): Promise<boolean> {
    // Anthropic doesn't have a public health endpoint
    // We consider it healthy if the last request succeeded
    // or if we haven't made any requests yet
    if (this.stats.totalRequests === 0) {
      return true;
    }

    return this.health.status !== "unhealthy";
  }
}

/**
 * Create an Anthropic provider from config
 */
export function createAnthropicProvider(
  config: ProviderConfig
): AnthropicProvider {
  return new AnthropicProvider(config);
}
